import { useTheme } from "../contexts/ThemeContext";

/**
 * Colori per i grafici (Recharts) e gli SVG disegnati a mano.
 *
 * Gli attributi SVG (`stroke`, `fill`) e gli stili inline dei tooltip non
 * passano dalle classi Tailwind, quindi i token CSS del tema non li
 * raggiungono: vanno scelti in JS. Questo hook e' l'unico punto in cui
 * quella scelta viene fatta.
 *
 * I valori del tema scuro sono esattamente quelli che erano scritti nei
 * componenti prima che il tema chiaro esistesse: lo scuro non cambia.
 * Nel chiaro le serie usano varianti piu' sature del brand, perche' su
 * fondo bianco il volt (1,9:1) e soprattutto l'acid (1,2:1) sparirebbero.
 */
const PALETTE = {
	dark: {
		grid: "#27272A",
		gridSoft: "#2f2f2f",
		axis: "#A1A1AA",
		axisMuted: "#9CA3AF",
		axisFaint: "#6B7280",
		tooltipBg: "#1A1A1A",
		tooltipBorder: "#27272A",
		tooltipText: "#FFFFFF",
		surface: "#0A0A0A",
		volt: "#00D68F",
		acid: "#CCFF00",
		blue: "#007AFF",
		blaze: "#FF3B30",
		amber: "#F97316",
		yellow: "#facc15",
	},
	light: {
		grid: "#E4E4E7",
		gridSoft: "#DEDEDE",
		axis: "#6B6B6B",
		axisMuted: "#6B6B6B",
		axisFaint: "#8A8A8A",
		tooltipBg: "#FFFFFF",
		tooltipBorder: "#D4D4D8",
		tooltipText: "#121212",
		surface: "#FFFFFF",
		volt: "#00A36E",
		acid: "#7A9900",
		blue: "#0062CC",
		blaze: "#D32319",
		amber: "#C2570A",
		yellow: "#A16207",
	},
};

export function useChartColors() {
	const { theme } = useTheme();
	return PALETTE[theme] || PALETTE.dark;
}

/** Stile pronto per `contentStyle` dei tooltip Recharts. */
export function useChartTooltipStyle() {
	const c = useChartColors();
	return {
		backgroundColor: c.tooltipBg,
		border: `1px solid ${c.tooltipBorder}`,
		borderRadius: "4px",
		fontSize: 12,
		color: c.tooltipText,
	};
}

/* ------------------------------------------------------------------ */

function parseHex(hex) {
	const h = String(hex || "")
		.trim()
		.replace("#", "");
	const full =
		h.length === 3
			? h
					.split("")
					.map((c) => c + c)
					.join("")
			: h;
	if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
	return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
}

function relativeLuminance([r, g, b]) {
	const ch = [r, g, b]
		.map((v) => v / 255)
		.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
	return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

function contrastOnWhite(rgb) {
	return 1.05 / (relativeLuminance(rgb) + 0.05);
}

/**
 * Rende leggibile su fondo chiaro un colore scelto dall'utente.
 *
 * I colori di mesocicli, fasi e domini del profilo prestativo sono dati:
 * li sceglie il coach e stanno su Mongo, quindi sono tarati sul fondo nero
 * con cui l'app e' nata. Usati come colore del TESTO su una card bianca,
 * i piu' chiari (l'acid #CCFF00 sta a 1,2:1) diventano illeggibili.
 *
 * Qui il colore viene scurito mescolandolo col nero, a passi, fino a
 * raggiungere 4,5:1 sul bianco. La tinta resta riconoscibile: cambia solo
 * la luminosita', quel tanto che serve. Nel tema scuro il colore torna
 * esattamente quello salvato.
 */
export function readableOnLight(color, theme) {
	if (theme !== "light") return color;
	const rgb = parseHex(color);
	if (!rgb) return color;

	let factor = 1;
	let current = rgb;
	while (contrastOnWhite(current) < 4.5 && factor > 0.05) {
		factor -= 0.05;
		current = rgb.map((v) => Math.round(v * factor));
	}
	return "#" + current.map((v) => v.toString(16).padStart(2, "0")).join("");
}

/** Versione hook: applica il tema corrente senza doverlo passare a mano. */
export function useReadableColor() {
	const { theme } = useTheme();
	return (color) => readableOnLight(color, theme);
}
